/**
 * PinterestConnectionExtras — sous-composant `<PlatformConnectionCard>`
 * spécifique Pinterest (S7-PR2). Trois affordances :
 *
 *   1. **Bandeau Trial** — affiché tant que `is_standard=false`. Pinterest
 *      n'a pas encore accordé à l'app le statut Standard ; les Pins
 *      publiés ne sont visibles que par l'utilisatrice loggée. Wording
 *      orienté utilisatrice ("toi"), pas dev.
 *
 *   2. **Badge "Quota du jour : X/Y"** — affiché toujours (Trial OU
 *      Standard, le quota Pinterest s'applique dans les deux cas). Reset
 *      implicite à 00:00 UTC côté BE. Si la query est en flight ou
 *      échoue, on n'affiche pas le badge plutôt que d'afficher un
 *      placeholder bruyant.
 *
 *   3. **Bouton "Marquer Standard"** — visible uniquement si
 *      `is_standard=false`. Idempotent côté BE (un second appel = no-op
 *      audit-only). Le clic ouvre un `useConfirm` car la décision est à
 *      ne prendre qu'après confirmation officielle Pinterest (l'auto-flip
 *      403 re-flippera False côté adapter si Pinterest rétrograde l'app).
 *
 * Si Pinterest accorde Standard, la card affiche à la place un badge
 * neutre "Standard" — pas de Mark Standard button orphelin qui ne fait
 * plus rien.
 *
 * Verrous tenus : pas de hex direct (tokens DS via classes), pas de
 * `<button>` natif (`<Button>` primitive), pas de `window.confirm`
 * (`useConfirm`).
 */

import { CheckCircle2, Info, Loader2, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useMarkPinterestConnectionStandard,
  usePinterestQuotaState,
} from '@/hooks/usePlatformConnections'

import './PinterestConnectionExtras.css'

interface PinterestConnectionExtrasProps {
  connectionId: number
  isStandard: boolean
}

export default function PinterestConnectionExtras({
  connectionId,
  isStandard,
}: PinterestConnectionExtrasProps) {
  const quotaQuery = usePinterestQuotaState()
  const markStandardMutation = useMarkPinterestConnectionStandard()
  const { confirm } = useConfirm()

  const handleMarkStandard = async () => {
    const ok = await confirm({
      title: 'Marquer cette connexion Pinterest comme Standard ?',
      message:
        'À ne faire qu’après que Pinterest t’a notifié officiellement de l’accord ' +
        'Standard pour l’app. Le flag sera re-flippé automatiquement à Trial si Pinterest ' +
        'rétrograde l’app par la suite.',
      variant: 'default',
      confirmText: 'Marquer Standard',
    })
    if (!ok) return
    try {
      await markStandardMutation.mutateAsync(connectionId)
      toast.success('Connexion Pinterest marquée Standard.')
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Bascule Standard impossible.'
      toast.error(detail)
    }
  }

  const quota = quotaQuery.data
  const quotaLabel = quota ? `Quota du jour : ${quota.used} / ${quota.cap}` : null
  // Pinterest API v5 plafonne en Trial — on prévient visuellement avant
  // d'arriver au refuse_zone (90 %). Warning_zone = 50 %.
  const quotaVariant: 'default' | 'warning' | 'danger' = quota?.is_refuse_zone
    ? 'danger'
    : quota?.is_warning_zone
      ? 'warning'
      : 'default'

  return (
    <section
      className="pinterest-extras"
      aria-label="Pinterest — statut Standard et quota journalier"
    >
      {!isStandard ? (
        <div className="pinterest-extras-trial-banner" role="status">
          <Info aria-hidden="true" size={16} />
          <p className="pinterest-extras-trial-message">
            <strong>Mode Trial.</strong> Pinterest n’a pas encore accordé l’accès Standard à l’app —
            les Pins que tu publies sont visibles uniquement par ton compte (pas par tes followers
            ni dans les recherches). Une fois Pinterest a validé Standard, clique sur{' '}
            <em>Marquer Standard</em> ci-dessous pour activer la diffusion publique.
          </p>
        </div>
      ) : null}

      <div className="pinterest-extras-row">
        {quotaLabel ? (
          <Badge
            variant={quotaVariant}
            size="sm"
            title="Cap journalier Pinterest API v5. Reset à 00:00 UTC."
          >
            {quotaLabel}
          </Badge>
        ) : null}

        {isStandard ? (
          <Badge variant="success" size="sm" title="App reconnue Standard par Pinterest.">
            <CheckCircle2 aria-hidden="true" size={14} />
            Standard
          </Badge>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleMarkStandard}
            disabled={markStandardMutation.isPending}
          >
            {markStandardMutation.isPending ? (
              <Loader2 aria-hidden="true" size={14} className="platform-spinner" />
            ) : (
              <ShieldCheck aria-hidden="true" size={14} />
            )}
            Marquer Standard
          </Button>
        )}
      </div>
    </section>
  )
}
